import React, { useState, useEffect, useRef } from "react";
import { TestSetService } from "../../../../services/TestSetService";
import { usePermissions } from "../../../../PermissionsProvider";
import RequestsTable from "../../../shared/RequestsTable";

interface ProdSeedPickerProps {
  intentGroupId: number;
  selectedRequestIds: string[];
  onSelectionChange: (requestIds: string[]) => void;
  minRequired?: number;
}

const ProdSeedPicker: React.FC<ProdSeedPickerProps> = ({
  intentGroupId,
  selectedRequestIds,
  onSelectionChange,
  minRequired = 5,
}) => {
  const { apiKey } = usePermissions();
  const [requests, setRequests] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(5);
  const [loading, setLoading] = useState(false);
  const [requestsTotal, setRequestsTotal] = useState(0);
  const [initialLoadDate] = useState(() => Date.now());
  const pageCacheRef = useRef<Record<number, any[]>>({});

  useEffect(() => {
    pageCacheRef.current = {};
    setRequests([]);
    setRequestsTotal(0);
    setCurrentPage(1);
  }, [intentGroupId]);

  const fetchPage = (page: number) => {
    if (pageCacheRef.current[page]) {
      setRequests(pageCacheRef.current[page]);
      return;
    }
    const offset = (page - 1) * pageSize;
    setLoading(true);
    TestSetService.getEligibleTestSetRequests(
      intentGroupId,
      -1,
      initialLoadDate,
      offset,
      pageSize,
      null,
      setLoading,
      apiKey
    ).subscribe(
      (data: any) => {
        const items = data.items || [];
        const total = data.total || 0;
        setRequests(items);
        setRequestsTotal(total);
        pageCacheRef.current = { ...pageCacheRef.current, [page]: items };
        setLoading(false);
      },
      () => setLoading(false)
    );
  };

  useEffect(() => {
    fetchPage(currentPage);
  }, [currentPage, intentGroupId, pageSize]);

  const meetsMinimum = selectedRequestIds.length >= minRequired;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">
          {selectedRequestIds.length} selected (minimum {minRequired} required)
        </span>
        {!meetsMinimum && (
          <span className="text-amber-500 font-gc-reg">
            Select at least {minRequired} examples to continue
          </span>
        )}
      </div>
      <RequestsTable
        entityType="intentGroup"
        entityId={intentGroupId}
        requests={requests}
        requestsTotal={requestsTotal}
        currentPage={currentPage}
        pageSize={pageSize}
        loading={loading}
        onPageChange={setCurrentPage}
        onFetchRequests={fetchPage}
        apiKey={apiKey}
        selectable={true}
        selectedRequestIds={selectedRequestIds}
        onSelectionChange={onSelectionChange}
        showApplicationColumn={false}
        hiddenColumns={["tags", "ttft", "confidence"]}
      />
    </div>
  );
};

export default ProdSeedPicker;
